import { Box, Typography } from '@mui/material'
import CodeBlock from '../CodeBlock'
import InlineCode from './InlineCode'
import { blockquoteSx, headingSx, hrSx, listSx, paragraphSx } from './markdownSx'
import { withCitations } from './withCitations'

/**
 * Block-level overrides. Headings become real `<h2>`/`<h3>` (P4.3) — the old
 * renderer emitted `<Typography component="p">` for every level, so responses
 * had no heading structure at all for a screen reader.
 *
 * `#` is mapped to `<h2>`, not `<h1>`: the page already owns the single `<h1>`,
 * and an assistant message must not outrank it.
 */
function makeHeading(level, cite) {
  return function MarkdownHeading({ children }) {
    return (
      <Typography component={`h${Math.min(level + 1, 6)}`} sx={headingSx(level + 1)}>
        {cite(children, `h${level}`)}
      </Typography>
    )
  }
}

function makeCode() {
  return function MarkdownCode({ className, children }) {
    const text = String(children ?? '').replace(/\n$/, '')
    if (!className && !text.includes('\n')) return <InlineCode>{text}</InlineCode>
    return <CodeBlock code={text} language={/language-(\w+)/.exec(className ?? '')?.[1] ?? ''} />
  }
}

export function blockComponents(citations) {
  const cite = (children, key) => withCitations(children, citations, key)

  return {
    h1: makeHeading(1, cite),
    h2: makeHeading(1, cite),
    h3: makeHeading(2, cite),
    h4: makeHeading(3, cite),
    h5: makeHeading(3, cite),
    h6: makeHeading(3, cite),
    p: ({ children }) => <Typography component="p" sx={paragraphSx}>{cite(children, 'p')}</Typography>,
    ul: ({ children }) => <Box component="ul" sx={listSx}>{children}</Box>,
    ol: ({ children }) => <Box component="ol" sx={listSx}>{children}</Box>,
    li: ({ children }) => <Box component="li" sx={{ font: 'inherit' }}>{cite(children, 'li')}</Box>,
    blockquote: ({ children }) => <Box component="blockquote" sx={blockquoteSx}>{children}</Box>,
    hr: () => <Box component="hr" sx={hrSx} />,
    // react-markdown wraps fenced code in <pre><code>; CodeBlock renders its
    // own <pre>, so the outer one is dropped to avoid nesting two.
    pre: ({ children }) => children,
    code: makeCode(),
  }
}
